import {
  ICollection,
  ICollectionTag,
  ILogger,
  IMetafields,
  IPost,
  IProduct,
  IReplacementProduct,
  envType,
} from '@monofront/types';
import {
  SearchOptions,
  SearchResponse,
} from 'algoliasearch-helper/types/algoliasearch';
import {
  generateCollectionIndexName,
  generateProductIndexName,
} from '../../utils';

import {
  ALGOLIA_PRODUCT_SORT_BY,
  CONFIG_ALGOLIA_GROUPING_DEBOUNCER_TIME,
  Channel,
  OOS_RECOMMENDATIONS_WIDGET_ID,
} from '../../constants';
import {
  GET_MERCH_RECOMMENDATIONS,
  GET_PRODUCT_BY_HANDLE,
} from '../../queries';
import { GrowthbookService } from '../growthbook';
import { AlgoliaSearchClient, AlgoliaSearchDebouncer } from '../algolia';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

export type IAlgoliaSearchProductsResponse = SearchResponse<IProduct>;
export type IAlgoliaSearchCollectionResponse = SearchResponse<ICollection>;
export type IAlgoliaSearchTipsResponse = SearchResponse<IPost>;

interface IAlgoliaConfig {
  environment: envType;
  index: {
    blog: string;
  };
}

export class ProductService {
  constructor(
    private algoliaConfig: IAlgoliaConfig,
    private algoliaSearchClient: AlgoliaSearchClient,
    private growthbookService: GrowthbookService,
    private logger: ILogger,
    private initApollo: (
      channel: Channel,
    ) => ApolloClient<NormalizedCacheObject>,
  ) {}

  private debouncer: AlgoliaSearchDebouncer | null = null;

  private async search<TResponse>(indexName: string, options: SearchOptions) {
    if (!this.debouncer || this.debouncer.isClosed()) {
      const debouncerTimeout =
        this.growthbookService.growthbook.getFeatureValue<number>(
          CONFIG_ALGOLIA_GROUPING_DEBOUNCER_TIME,
          50,
        );

      this.debouncer = new AlgoliaSearchDebouncer(
        this.algoliaSearchClient.searchClient,
        debouncerTimeout,
      );
    }

    return this.debouncer.search<TResponse>({
      indexName,
      params: options,
    });
  }

  private getProductIndex = (channel: Channel = Channel.FR): string => {
    return generateProductIndexName(
      this.algoliaConfig.environment,
      false,
      channel,
      false,
      undefined,
    );
  };

  private getProductPopularityIndex = (
    channel: Channel = Channel.FR,
  ): string => {
    return generateProductIndexName(
      this.algoliaConfig.environment,
      false,
      channel,
      false,
      ALGOLIA_PRODUCT_SORT_BY.POPULARITY,
    );
  };

  private getOOSProductIndex = (channel: Channel = Channel.FR): string => {
    return generateProductIndexName(
      this.algoliaConfig.environment,
      false,
      channel,
      false,
      undefined,
      true,
    );
  };

  private getCollectionIndex = (locale = 'fr'): string => {
    return generateCollectionIndexName(this.algoliaConfig.environment, locale);
  };

  public getProductWithTips = async (
    searchValue: string,
    tipsCount = 2,
    channel: Channel = Channel.FR,
    attributesToRetrieve?: readonly string[],
  ): Promise<{ product?: IProduct; tips?: IPost[] }> => {
    const [productsResponse, tipsResponse] = await Promise.all([
      this.search<IProduct>(
        generateProductIndexName(
          this.algoliaConfig.environment,
          false,
          channel,
        ),
        {
          hitsPerPage: 1,
          filters: `handle:${searchValue}`,
          ...(attributesToRetrieve
            ? { attributesToRetrieve: [...attributesToRetrieve] }
            : {}),
        },
      ),
      this.search<IPost>(this.algoliaConfig.index.blog, {
        hitsPerPage: tipsCount,
        filters: `products_handle:"${searchValue}"`,
      }),
    ]);

    const product: IProduct | undefined = productsResponse.hits.find(
      (product) => product.handle === searchValue,
    );

    return { product, tips: tipsResponse.hits };
  };

  public getProductPageIndex(channel: Channel = Channel.FR) {
    return generateProductIndexName(
      this.algoliaConfig.environment,
      false,
      channel,
    );
  }

  /**
   * Mostly used to retrieve products from there object id
   */
  public getProducts = async ({
    filters,
    hitsPerPage = 20,
    page = 0,
    ruleContexts = [],
    channel = Channel.FR,
  }: SearchOptions & {
    channel?: Channel;
  }) => {
    const indexName = this.getProductIndex(channel);

    return this.search<IProduct>(indexName, {
      filters,
      hitsPerPage,
      page,
      ruleContexts,
    });
  };

  /**
   * Used for collections and carousels
   */
  public getProductsByPopularity = async ({
    filters,
    hitsPerPage = 20,
    page = 0,
    ruleContexts = [],
    channel = Channel.FR,
    userToken,
  }: SearchOptions & {
    channel?: Channel;
  }) => {
    const indexName = this.getProductPopularityIndex(channel);

    return this.search<IProduct>(indexName, {
      filters,
      hitsPerPage,
      page,
      ruleContexts,
      userToken,
    });
  };

  public getProductsBySku = async (
    skus: string[],
    channel: Channel,
    skipProductCache = false,
  ): Promise<IProduct[]> => {
    if (!skus.length) {
      return [];
    }

    if (!skipProductCache) {
      const cachedProducts = skus.map((sku) =>
        this.algoliaSearchClient.cache.get(sku),
      );

      if (cachedProducts.every((cachedProduct) => cachedProduct)) {
        return cachedProducts as Array<IProduct>;
      }
      // If we have at least one product not in cache, we fetch all requested (it doesn't cost more)
    }

    const algoliaSearchQuery = skus.join(' OR sku:');
    const filters = `sku:${algoliaSearchQuery}`;

    const { hits } = await this.getProducts({
      filters,
      hitsPerPage: skus.length,
      channel,
    });

    return hits;
  };

  public getMerchRecommendationForOOS = async (
    channel: Channel,
    sku: string,
    minimumQuantity: number,
    apolloClient: ApolloClient<NormalizedCacheObject>,
  ): Promise<{ replacement: IProduct; widgetId: string } | null> => {
    let widgetId = '';

    try {
      widgetId = this.growthbookService.getFeatureValueWithoutTracking<string>(
        ...OOS_RECOMMENDATIONS_WIDGET_ID,
      );

      if (!widgetId) return Promise.resolve(null);

      const { data, errors } = await apolloClient.query({
        query: GET_MERCH_RECOMMENDATIONS,
        variables: {
          widgetId,
          variables: {
            sku,
            cart: {
              skus: [],
            },
            url: null,
          },
        },
        fetchPolicy: 'cache-first',
        context: {
          keyFields: ['sku'],
        },
      });

      if (errors?.length)
        throw new Error(
          `Graphql error: ${
            errors[0]?.message
          } - Path: ${errors[0]?.path?.toString()}`,
        );

      if (!data?.getRecommendationsV6?.recommendations?.length) {
        return Promise.resolve(null);
      }

      const recos = data.getRecommendationsV6.recommendations;
      const maxToVerify = recos.length > 5 ? 5 : recos.length;
      const oosProductsIndex = this.getOOSProductIndex(channel);

      for (let i = 0; i < maxToVerify; i++) {
        const { hits: foundProductByHandle } = await this.search<IProduct>(
          oosProductsIndex,
          {
            filters: `handle:${recos[i].handle} AND inventory_quantity >= ${minimumQuantity}`,
            hitsPerPage: 1,
          },
        );

        if (foundProductByHandle.length) {
          return {
            widgetId,
            replacement: foundProductByHandle[0],
          };
        }
      }

      return Promise.resolve(null);
    } catch (error) {
      this.logger.error('Cannot get recommendations for OOS product', {
        widget_id: widgetId,
        sku,
        error,
      });

      return Promise.resolve(null);
    }
  };

  public getReplacementProduct = async (
    sku: string,
    title: string,
    quantity: number,
    collection?: string,
    channel = Channel.FR,
  ): Promise<IReplacementProduct | null> => {
    const minimumQuantity = quantity > 0 ? quantity : 1; // just because in preprod we have -XXX for some products :')

    // 1. First, try to get results from Prismic Merch Widget
    const recommendationResponse = await this.getMerchRecommendationForOOS(
      channel,
      sku,
      minimumQuantity,
      this.initApollo(channel),
    );

    if (recommendationResponse)
      return {
        replacementSource: `widget:${recommendationResponse.widgetId}`,
        ...recommendationResponse.replacement,
      };

    // 2. Otherwise, try to get results by searching the product name in Algolia
    const oosProductsIndex = this.getOOSProductIndex(channel);

    const { hits: foundProductsByName } = await this.search<IProduct>(
      oosProductsIndex,
      {
        query: title,
        filters: `inventory_quantity >= ${minimumQuantity}`,
        hitsPerPage: 1,
        removeWordsIfNoResults: channel === Channel.DE ? 'none' : 'lastWords',
      },
    );

    if (foundProductsByName.length) {
      return {
        replacementSource: 'algolia-name-search',
        ...foundProductsByName[0],
      };
    }

    // 3. If still no results, try to expand the search to the closest collection in Algolia
    if (collection) {
      const { hits: foundProductsByCollection } = await this.search<IProduct>(
        oosProductsIndex,
        {
          query: collection,
          filters: `inventory_quantity >= ${minimumQuantity}`,
          hitsPerPage: 1,
        },
      );

      if (foundProductsByCollection.length) {
        return {
          replacementSource: 'algolia-collection-search',
          ...foundProductsByCollection[0],
        };
      }
    }

    // 4. Time to give up
    return null;
  };

  public getCollection = async ({
    query,
    fetchChildren,
    locale = 'fr',
  }: {
    query: { handle?: string | string[] };
    fetchChildren?: boolean;
    locale?: string;
  }): Promise<ICollection | undefined> => {
    let filters = `handle:"${query.handle}"`;

    if (fetchChildren && query.handle) {
      filters = `${filters} OR parent:"${query.handle}"`;
    }

    const collectionIndexName = this.getCollectionIndex(locale);

    const collections = await this.search<ICollection>(collectionIndexName, {
      filters,
    });

    const collection = collections.hits.find(
      (collection: ICollection) => collection.handle === query.handle,
    );

    if (collection && fetchChildren && query.handle) {
      const childCollections = collections.hits.reduce(
        (children: ICollectionTag[], currentCollection: ICollection) => {
          return currentCollection.parent === query.handle
            ? [
                ...children,
                {
                  handle: currentCollection.handle,
                  omsId: currentCollection.objectID,
                  title: currentCollection.titleLink || currentCollection.title,
                  _tags: currentCollection._tags,
                },
              ]
            : children;
        },
        [],
      );

      if (childCollections.length)
        collection.childCollections = childCollections;
    }

    return collection;
  };

  public getCollections = async ({
    query,
    locale = 'fr',
  }: {
    query: { parent?: string; exclude?: string[] };
    locale?: string;
  }): Promise<ICollection[] | undefined> => {
    const exclusionRule = query.exclude?.length
      ? query.exclude.reduce((rules, elementToExclude) => {
          return `${rules} ${rules ? 'AND' : ''} NOT ${elementToExclude}`;
        }, '')
      : '';
    const parentRule = query.parent ? `parent:"${query.parent}"` : '';

    const collectionIndexName = this.getCollectionIndex(locale);

    const collections = await this.search<ICollection>(collectionIndexName, {
      filters: `${parentRule} ${parentRule ? 'AND' : ''} ${exclusionRule}`,
    });

    return collections.hits;
  };

  public getProductByHandle = async (
    handle: string,
    channel: Channel,
  ): Promise<IMetafields | null> => {
    const apolloClient = this.initApollo(channel);

    const { data } = await apolloClient.query({
      query: GET_PRODUCT_BY_HANDLE,
      variables: {
        handle,
      },
    });

    const product = data.getProductByHandle;

    if (!product?.metafields?.length) {
      return null;
    }

    return product;
  };
}
